import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  hackerChecklistService,
  HackerChecklist,
  ChecklistProgress
} from '../../lib/hackerChecklistsService';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

export const HackerChecklistDetailPage: React.FC = () => {
  const { checklistId } = useParams<{ checklistId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();

  const [checklist, setChecklist] = useState<HackerChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<ChecklistProgress | null>(null);
  const [completedItemIds, setCompletedItemIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isNewSession = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('newSession') === 'true';
  }, [location.search]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/signin');
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    if (!checklistId) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await hackerChecklistService.getChecklistById(checklistId);
        if (!data) {
          setError('Checklist not found or is no longer available.');
          return;
        }
        setChecklist(data);

        if (!isNewSession) {
          const savedProgress = await hackerChecklistService.getUserProgress(checklistId);
          if (savedProgress) {
            setProgress(savedProgress);
            setCompletedItemIds(savedProgress.completedItemIds);
          }
        } else {
          setProgress(null);
          setCompletedItemIds([]);
        }
      } catch (err) {
        console.error('Failed to load checklist detail', err);
        setError('Failed to load checklist. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [checklistId, isNewSession]);

  const totalItems = useMemo(() => {
    if (!checklist) return 0;
    return checklist.sections.reduce((total, section) => total + section.items.length, 0);
  }, [checklist]);

  const completedCount = completedItemIds.length;
  const completionPercentage = totalItems === 0 ? 0 : Math.round((completedCount / totalItems) * 100);

  const toggleItem = useCallback(async (itemId: string) => {
    if (!checklistId) return;
    const newIds = completedItemIds.includes(itemId)
      ? completedItemIds.filter((id) => id !== itemId)
      : [...completedItemIds, itemId];

    setCompletedItemIds(newIds);

    try {
      setIsSaving(true);
      setError(null);
      await hackerChecklistService.saveUserProgress(checklistId, newIds);
      setProgress({
        checklistId,
        userId: auth.currentUser?.uid || '',
        completedItemIds: newIds,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Failed to save checklist progress', err);
      setError('Unable to save progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [auth, checklistId, completedItemIds]);

  const markAll = async () => {
    if (!checklistId || !checklist) return;
    const allIds = checklist.sections.flatMap((section) => section.items.map((item) => item.id));
    try {
      setIsSaving(true);
      setCompletedItemIds(allIds);
      await hackerChecklistService.saveUserProgress(checklistId, allIds);
      setProgress({
        checklistId,
        userId: auth.currentUser?.uid || '',
        completedItemIds: allIds,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Failed to mark all items complete', err);
      setError('Unable to mark items complete.');
    } finally {
      setIsSaving(false);
    }
  };

  const resetProgress = async () => {
    if (!checklistId) return;
    try {
      setIsSaving(true);
      setCompletedItemIds([]);
      await hackerChecklistService.saveUserProgress(checklistId, []);
      setProgress({
        checklistId,
        userId: auth.currentUser?.uid || '',
        completedItemIds: [],
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Failed to reset progress', err);
      setError('Unable to reset progress.');
    } finally {
      setIsSaving(false);
    }
  };

  const exportMarkdown = () => {
    if (!checklist) return;
    const lines: string[] = [];
    lines.push(`# ${checklist.title}`);
    if (checklist.description) {
      lines.push('', checklist.description);
    }
    lines.push(`
Category: ${checklist.category || 'general'}
Difficulty: ${checklist.difficulty || 'n/a'}
Progress: ${completedCount}/${totalItems} (${completionPercentage}%)
Updated: ${checklist.updatedAt}
`);

    checklist.sections.forEach((section, idx) => {
      lines.push(`
## ${idx + 1}. ${section.title}`);
      if (section.objective) {
        lines.push(`> Objective: ${section.objective}`);
      }
      section.items.forEach((item, itemIndex) => {
        const isComplete = completedItemIds.includes(item.id);
        const checkbox = isComplete ? '[x]' : '[ ]';
        lines.push(`- ${checkbox} ${itemIndex + 1}. ${item.text}`);
        if (item.referenceUrl) {
          lines.push(`  - Reference: ${item.referenceUrl}`);
        }
        if (item.toolHint) {
          lines.push(`  - Tool Hint: \
    ${item.toolHint}`);
        }
      });
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${checklist.title.replace(/\s+/g, '-').toLowerCase()}-playbook.md`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Preparing checklist…</div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">{error || 'Checklist not found.'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <Button variant="outline" onClick={() => navigate('/hacker-checklists')}>
              Back to Checklists
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="space-y-2">
              <h1 className="font-['Space_Grotesk'] font-bold text-3xl text-black">
                {checklist.title}
              </h1>
              <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                <span className="px-2 py-1 bg-gray-100 rounded-full">
                  Category: {checklist.category || 'general'}
                </span>
                {checklist.difficulty && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {checklist.difficulty}
                  </span>
                )}
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                  Status: {checklist.status}
                </span>
                <span className="px-2 py-1 bg-gray-200 rounded-full">
                  Updated {new Date(checklist.updatedAt).toLocaleDateString()}
                </span>
              </div>
              {checklist.description && (
                <p className="text-sm text-gray-700 max-w-3xl">{checklist.description}</p>
              )}
              {checklist.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                  {checklist.tags.map((tag) => (
                    <span key={tag} className="bg-gray-100 rounded-full px-2 py-1">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2 min-w-[220px]">
              <div className="rounded-lg border px-4 py-3 bg-gray-50">
                <p className="text-xs text-gray-500 uppercase">Overall Progress</p>
                <p className="text-xl font-semibold text-black">
                  {completedCount} / {totalItems} ({completionPercentage}%)
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={exportMarkdown}>
                  Export Markdown
                </Button>
                <Button variant="outline" onClick={markAll} disabled={isSaving}>
                  Mark All Complete
                </Button>
                <Button variant="ghost" onClick={resetProgress} disabled={isSaving}>
                  Reset Progress
                </Button>
              </div>
              {isSaving && <p className="text-xs text-gray-500">Saving progress…</p>}
              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
          </div>

          <div className="space-y-6">
            {checklist.sections.map((section, sectionIndex) => (
              <div key={section.id} className="border border-[#E7E7E7] rounded-[16px] p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
                  <div>
                    <h2 className="font-['Space_Grotesk'] font-semibold text-xl text-black">
                      {sectionIndex + 1}. {section.title}
                    </h2>
                    {section.objective && (
                      <p className="text-sm text-gray-600">Objective: {section.objective}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {section.items.length} item{section.items.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="space-y-3">
                  {section.items.map((item) => {
                    const checked = completedItemIds.includes(item.id);
                    return (
                      <div key={item.id} className={`flex flex-col md:flex-row md:items-start md:justify-between gap-3 border rounded-lg p-4 ${checked ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4"
                            checked={checked}
                            onChange={() => toggleItem(item.id)}
                          />
                          <div>
                            <p className="text-sm text-black font-medium leading-relaxed">{item.text}</p>
                            <div className="space-y-1 mt-2 text-xs text-gray-600">
                              {item.referenceUrl && (
                                <p>
                                  Reference:{' '}
                                  <a href={item.referenceUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                                    {item.referenceUrl}
                                  </a>
                                </p>
                              )}
                              {item.toolHint && (
                                <p className="font-mono bg-white border rounded px-2 py-1">
                                  {item.toolHint}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.required ? 'Required' : 'Optional'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HackerChecklistDetailPage;

